// glonet-react-tailwind-project--src--components--AffiliatesLanding.js
// Public recruiting page for the glonet.ai affiliate program (Partner/Standard
// tiers), served
// full-bleed at /affiliates as a native sibling of HostsLanding. The numbers
// (percent, window, Partner slots left) come live from
// GET public/affiliates/program/<GLONET_AI_EVENT_ID>; hard fallbacks mirror
// the seeded settings (Partner 50% x 12mo x 100, standard 25% x 6mo).
// "Apply for a slot" requires being signed in (affiliates are members):
// signed-out visitors round-trip through /signin and come back with ?apply=1.
// Copy law: no em or en dashes anywhere a visitor sees.
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import { GLONET_AI_EVENT_ID } from "../config/glonetai";
import AffiliateApplyDialog from "./AffiliateApplyDialog";

const ACCENT = "#74a0f5";
const ACCENT_STRONG = "#5b8cf0";
const GLOW = "rgba(116,160,245,0.22)";
const MONO = "'JetBrains Mono',monospace";
const SERIF = "'Instrument Serif',serif";

const FALLBACK_PROGRAM = {
  partner_pct: 50, partner_window_months: 12, partner_slots: 100,
  partner_slots_left: null, default_affiliate_pct: 25, default_window_months: 6,
  self_serve_enabled: true,
};
const EST_PRICE = 20; // the working number; copy says "estimated"
// Every affiliate link carries a first-year member discount (owner ruling
// 2026-08-11): 60% (default) or 40%, the affiliate's pick. The calculator
// has the same switch so the math never overstates.
const REF_DISCOUNT_PCT = 60;

export default function AffiliatesLanding() {
  const navigate = useNavigate();
  const location = useLocation();
  const [program, setProgram] = useState(FALLBACK_PROGRAM);
  const [applyOpen, setApplyOpen] = useState(false);
  const [myStatus, setMyStatus] = useState(null); // none|pending|approved|rejected
  const [myCode, setMyCode] = useState(null);
  // The calculator asks for what an affiliate KNOWS (their follower count) and
  // lets them set the two rates, instead of making them do the arithmetic in
  // their head. Two steps on purpose: a few messages getting 5% to join
  // something FREE is realistic; 5% of followers paying $20/mo is not the same
  // claim, and collapsing them into one number is how a calculator starts
  // lying. Owner's working numbers 2026-08-09.
  const [followers, setFollowers] = useState(40000);
  const [joinPct, setJoinPct] = useState(5);   // of followers, join free
  const [payPct, setPayPct] = useState(30);    // of those, still paying at Matching Day
  // the discount their link will carry (60 default, 40 optional); the same
  // choice they make for real on the dashboard after approval
  const [refDiscount, setRefDiscount] = useState(REF_DISCOUNT_PCT);

  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const onGlonetAiHost = host === "glonet.ai" || host === "www.glonet.ai";
  const landingPath = onGlonetAiHost ? "/" : "/glonet-ai";

  useEffect(() => {
    import("../services/api").then(({ default: api }) =>
      api.get(`public/affiliates/program/${GLONET_AI_EVENT_ID}`)
        .then(({ data }) => setProgram((p) => ({ ...p, ...data })))
        .catch(() => {}));
  }, []);

  const refreshMyStatus = () => {
    Auth.currentAuthenticatedUser()
      .then(() => import("../services/api"))
      .then(({ default: api }) =>
        api.get(`affiliates/my-application?event_id=${GLONET_AI_EVENT_ID}`)
          .then(({ data }) => {
            setMyStatus(data.status);
            if (data.affiliate_code) setMyCode(data.affiliate_code);
          }))
      .catch(() => {});
  };
  useEffect(refreshMyStatus, []);

  const applyForSlot = async (e) => {
    if (e) e.preventDefault();
    try {
      await Auth.currentAuthenticatedUser();
      setApplyOpen(true);
    } catch (err) {
      navigate("/signin", { state: { from: "/affiliates?apply=1" } });
    }
  };

  // Post-sign-in return leg: ?apply=1 reopens the window, then cleans the URL.
  useEffect(() => {
    if (new URLSearchParams(location.search).get("apply") !== "1") return;
    Auth.currentAuthenticatedUser()
      .then(() => setApplyOpen(true))
      .catch(() => {})
      .finally(() => navigate("/affiliates", { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const pct = program.partner_pct ?? program.founding_pct ?? 50;
  const months = program.partner_window_months ?? program.founding_window_months ?? 12;
  const slots = program.partner_slots ?? program.founding_slots;
  const left = program.partner_slots_left ?? program.founding_slots_left;
  const members = useMemo(() => Math.round(followers * (joinPct / 100)), [followers, joinPct]);
  const subs = useMemo(() => Math.round(members * (payPct / 100)), [members, payPct]);
  // Year-one price, not the $20 anchor: the whole payout window runs inside
  // the referred members' first-year discount, so $20 here would overstate.
  const year1Price = useMemo(() => Math.round(EST_PRICE * (1 - refDiscount / 100)), [refDiscount]);
  const monthly = useMemo(() => Math.round(subs * year1Price * (pct / 100)), [subs, year1Price, pct]);
  // What the whole cohort is worth while their windows run — the actual offer,
  // not the monthly slice of it.
  const windowTotal = useMemo(() => monthly * months, [monthly, months]);

  const statCard = (big, label, sub) => (
    <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: "18px", padding: "30px 32px", textAlign: "center" }}>
      <div style={{ fontFamily: MONO, fontSize: "clamp(38px,5vw,58px)", color: ACCENT, lineHeight: 1 }}>{big}</div>
      <div style={{ fontFamily: SERIF, fontSize: "21px", marginTop: 12 }}>{label}</div>
      {sub && <div style={{ fontSize: "14px", color: "#9a9ea7", marginTop: 8, lineHeight: 1.5 }}>{sub}</div>}
    </div>
  );

  const step = (n, title, body) => (
    <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: "18px", padding: "28px 30px" }}>
      <div style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.22em", color: ACCENT }}>{n}</div>
      <h3 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "22px", margin: "10px 0 10px" }}>{title}</h3>
      <p style={{ fontSize: "15px", lineHeight: 1.6, color: "#b6b9c0", margin: 0 }}>{body}</p>
    </div>
  );

  return (
    <div style={{ background: "#0c0d10", color: "#f1f2f4", minHeight: "100vh",
      fontFamily: "'Hanken Grotesk',system-ui,sans-serif", WebkitFontSmoothing: "antialiased",
      overflowX: "hidden", position: "relative" }}>
      <style>{`
        @keyframes al-breathe { 0%,100% { opacity: 0.55; transform: translateX(-50%) scale(1); } 50% { opacity: 0.8; transform: translateX(-50%) scale(1.06); } }
        .al-btn { transition:background .15s, border-color .15s; }
        .al-btn-primary:hover { background:${ACCENT_STRONG} !important; }
        .al-btn-ghost:hover { border-color:${ACCENT} !important; }
        .al-link { color:#b6b9c0; text-decoration:none; transition:color .15s; }
        .al-link:hover { color:#fff; }
        .al-more { transition: color .18s ease; }
        .al-more:hover { color: #cfd2d8; }
        .al-more svg { animation: al-nudge 2.4s ease-in-out infinite; }
        @keyframes al-nudge { 0%,100% { transform: translateY(0); } 50% { transform: translateY(5px); } }
        @media (prefers-reduced-motion: reduce) { .al-more svg { animation: none; } }
        /* accent-color alone leaves the track invisible on the black page in
           WebKit; draw the track and thumb explicitly. */
        input[type=range].al-range { -webkit-appearance:none; appearance:none; width:100%;
          height:22px; background:transparent; cursor:pointer; accent-color:${ACCENT}; }
        input[type=range].al-range::-webkit-slider-runnable-track {
          height:6px; border-radius:999px; background:rgba(255,255,255,0.22); }
        input[type=range].al-range::-webkit-slider-thumb { -webkit-appearance:none;
          width:18px; height:18px; border-radius:50%; background:${ACCENT};
          border:none; margin-top:-6px; }
        input[type=range].al-range::-moz-range-track {
          height:6px; border-radius:999px; background:rgba(255,255,255,0.22); }
        input[type=range].al-range::-moz-range-progress {
          height:6px; border-radius:999px; background:${ACCENT}; }
        input[type=range].al-range::-moz-range-thumb {
          width:18px; height:18px; border-radius:50%; background:${ACCENT}; border:none; }
        @media (max-width: 840px) {
          .al-grid-3, .al-grid-4 { grid-template-columns:1fr !important; gap:24px !important; }
        }
        @media (max-width: 480px) {
          .al-nav { padding: 20px 16px !important; }
          .al-nav-hosts, .al-nav-tag { display: none !important; }
          .al-nav-cta { font-size: 13px !important; padding: 9px 14px !important; }
        }
      `}</style>

      {/* ambient hero glow */}
      <div style={{ position: "absolute", top: "-220px", left: "50%", transform: "translateX(-50%)",
        width: "1000px", height: "680px",
        background: `radial-gradient(circle at center, ${GLOW} 0%, transparent 68%)`,
        filter: "blur(14px)", animation: "al-breathe 9s ease-in-out infinite" }} />

      {/* NAV */}
      <nav className="al-nav" style={{ position: "relative", zIndex: 3, maxWidth: "1140px", margin: "0 auto",
        padding: "26px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "24px" }}>
        <a href={landingPath} onClick={(e) => { e.preventDefault(); navigate(landingPath); }}
           style={{ display: "flex", alignItems: "baseline", gap: "12px", textDecoration: "none", color: "inherit" }}>
          <span style={{ fontFamily: SERIF, fontSize: "25px", letterSpacing: "-0.01em" }}>
            glonet<span style={{ color: ACCENT }}>.ai</span>
          </span>
          <span className="al-nav-tag" style={{ fontFamily: MONO, fontSize: "10px", letterSpacing: "0.26em", color: "#6b6f78", textTransform: "uppercase" }}>
            Partner&nbsp;Program
          </span>
        </a>
        <div style={{ display: "flex", alignItems: "center", gap: "18px" }}>
          <a href="/hosts" className="al-link al-nav-hosts" onClick={(e) => { e.preventDefault(); navigate("/hosts"); }}
             style={{ fontSize: "14px" }}>For hosts</a>
          <button onClick={applyForSlot} className="al-btn al-btn-primary al-nav-cta"
                  style={{ fontSize: "14px", fontWeight: 600, color: "#0c0d10", background: ACCENT,
                    border: "none", cursor: "pointer", padding: "10px 18px", borderRadius: "999px", whiteSpace: "nowrap" }}>
            Apply for a slot
          </button>
        </div>
      </nav>

      {/* HERO */}
      <section style={{ position: "relative", zIndex: 2, maxWidth: "1140px", margin: "0 auto",
        padding: "84px 32px 56px", textAlign: "center" }}>
        <div style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.26em", color: ACCENT,
          textTransform: "uppercase", marginBottom: "28px" }}>
          Affiliate program · early access{typeof left === "number" && slots ? ` · ${left} of ${slots} Partner slots open` : ""}
        </div>
        <h1 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(42px,6.4vw,82px)",
          lineHeight: 1.02, letterSpacing: "-0.015em", margin: "0 auto", maxWidth: "15ch", textWrap: "balance" }}>
          Bring your people. Keep half.
        </h1>
        <p style={{ fontSize: "19px", lineHeight: 1.6, color: "#b6b9c0", maxWidth: "52ch", margin: "26px auto 0" }}>
          Bring people to Glonet.ai and earn {pct}% of what they pay, every
          month, for {months} months each.
        </p>
        <div style={{ display: "flex", gap: "14px", justifyContent: "center", flexWrap: "wrap", marginTop: "40px" }}>
          <button onClick={applyForSlot} className="al-btn al-btn-primary"
                  style={{ fontSize: "15px", fontWeight: 600, color: "#0c0d10", background: ACCENT,
                    border: "none", cursor: "pointer", padding: "15px 24px", borderRadius: "999px" }}>
            Apply for a Partner slot
          </button>
          <button onClick={() => navigate("/affiliates/story")} className="al-btn al-btn-ghost"
                  style={{ fontSize: "15px", fontWeight: 600, color: "#f1f2f4", background: "transparent",
                    border: "1px solid rgba(255,255,255,0.25)", cursor: "pointer", padding: "15px 24px", borderRadius: "999px" }}>
            Watch the 60 second story
          </button>
        </div>
        {/* The first screen used to end on two buttons with nothing under them,
            so the page looked finished. This says it is not, and one tap puts
            the earnings calculator on screen. */}
        <button onClick={() => {
                  const el = document.getElementById("the-math");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                aria-label="See what your audience is worth"
                className="al-more"
                style={{ display: "block", margin: "44px auto 0", background: "transparent",
                  border: "none", cursor: "pointer", color: "#7e838c", padding: "6px 14px" }}>
          <span style={{ fontFamily: MONO, fontSize: 11, letterSpacing: ".22em",
            textTransform: "uppercase", display: "block", marginBottom: 8 }}>
            What that is worth
          </span>
          <svg width="26" height="15" viewBox="0 0 26 15" fill="none" aria-hidden="true"
               style={{ display: "block", margin: "0 auto" }}>
            <path d="M2 2 L13 12 L24 2" stroke="currentColor" strokeWidth="2.2"
                  strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {myStatus === "pending" && (
          <div style={{ marginTop: 22, fontFamily: MONO, fontSize: 12, color: "#e0a35e", letterSpacing: ".08em" }}>
            Your application is in. We review daily; watch your email.
          </div>
        )}
        {myStatus === "approved" && (
          <div style={{ marginTop: 22, fontFamily: MONO, fontSize: 12, color: "#57d9a4", letterSpacing: ".08em" }}>
            You are in. Your code: {myCode} ·{" "}
            <a href="/affiliates/dashboard" style={{ color: "#57d9a4" }}
               onClick={(e) => { e.preventDefault(); navigate("/affiliates/dashboard"); }}>
              open your dashboard
            </a>{" "}·{" "}
            <a href={`/affiliate-onboarding/${myCode}`} style={{ color: "#57d9a4" }}
               onClick={(e) => { e.preventDefault(); navigate(`/affiliate-onboarding/${myCode}`); }}>
              finish payout setup
            </a>
          </div>
        )}
      </section>

      <div style={{ maxWidth: "1140px", margin: "0 auto", height: "1px", background: "rgba(255,255,255,0.07)" }} />

      {/* THE MATH */}
      <section id="the-math" style={{ position: "relative", zIndex: 2, maxWidth: "1140px", margin: "0 auto", padding: "96px 32px" }}>
        <div style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.26em", color: "#6b6f78", textTransform: "uppercase" }}>The math</div>
        <div className="al-grid-3" style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "40px", marginTop: "30px", alignItems: "center" }}>
          <div>
            <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(28px,3.2vw,42px)", lineHeight: 1.1, margin: 0, maxWidth: "20ch" }}>
              What does your audience turn into?
            </h2>
            <p style={{ fontSize: "16px", lineHeight: 1.65, color: "#b6b9c0", margin: "18px 0 26px", maxWidth: "54ch" }}>
              Put in your following. Send your people a few messages &mdash; we
              write them for you &mdash; and set what you think happens.
            </p>

            <label style={{ display: "block", fontFamily: MONO, fontSize: 11, letterSpacing: ".18em",
              textTransform: "uppercase", color: "#6b6f78", marginBottom: 8 }}>
              People you can reach
            </label>
            <input type="number" min="100" max="10000000" step="1000" value={followers}
                   onChange={(e) => setFollowers(Math.max(0, Math.min(10000000, Number(e.target.value) || 0)))}
                   aria-label="people you can reach"
                   style={{ width: "100%", background: "rgba(255,255,255,0.04)", color: "#f1f2f4",
                     border: "1px solid rgba(255,255,255,0.14)", borderRadius: 10, padding: "12px 14px",
                     fontFamily: MONO, fontSize: 20 }} />

            <div style={{ marginTop: 26 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
                <label style={{ fontFamily: MONO, fontSize: 11, letterSpacing: ".18em",
                  textTransform: "uppercase", color: "#6b6f78" }}>
                  Who join (free until launch day)
                </label>
                <span style={{ fontFamily: MONO, fontSize: 13, color: ACCENT }}>
                  {joinPct}% &rarr; {members.toLocaleString()} members
                </span>
              </div>
              <input type="range" min="1" max="25" step="1" value={joinPct} className="al-range"
                     onChange={(e) => setJoinPct(Number(e.target.value))}
                     aria-label="percent of your followers who join"
                     style={{ marginTop: 8 }} />
            </div>

            <div style={{ marginTop: 22 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
                <label style={{ fontFamily: MONO, fontSize: 11, letterSpacing: ".18em",
                  textTransform: "uppercase", color: "#6b6f78" }}>
                  Who stay and pay
                </label>
                <span style={{ fontFamily: MONO, fontSize: 13, color: ACCENT }}>
                  {payPct}% &rarr; {subs.toLocaleString()} paying
                </span>
              </div>
              <input type="range" min="5" max="100" step="5" value={payPct} className="al-range"
                     onChange={(e) => setPayPct(Number(e.target.value))}
                     aria-label="percent of members who keep paying"
                     style={{ marginTop: 8 }} />
              <div style={{ fontSize: "13px", color: "#8a8f98", marginTop: 10, lineHeight: 1.55, maxWidth: "52ch" }}>
                Joining is free while the network fills. Everyone subscribes on
                launch day, when matching turns on &mdash; this is how many of
                yours you think stay.
              </div>
            </div>

            <div style={{ marginTop: 26 }}>
              <label style={{ display: "block", fontFamily: MONO, fontSize: 11, letterSpacing: ".18em",
                textTransform: "uppercase", color: "#6b6f78", marginBottom: 8 }}>
                Discount you offer your people
              </label>
              <div style={{ display: "flex", gap: 10 }}>
                {[[60, 8, "converts best"], [40, 12, "more per member"]].map(([d, price, tag]) => {
                  const active = refDiscount === d;
                  return (
                    <button key={d} className="al-btn" onClick={() => setRefDiscount(d)}
                            aria-pressed={active}
                            style={{ flex: 1, textAlign: "left", cursor: "pointer",
                              background: active ? "rgba(116,160,245,0.14)" : "rgba(255,255,255,0.04)",
                              border: active ? `1px solid ${ACCENT}` : "1px solid rgba(255,255,255,0.14)",
                              borderRadius: 10, padding: "10px 14px", color: "#f1f2f4" }}>
                      <span style={{ fontFamily: MONO, fontSize: 16, color: active ? ACCENT : "#f1f2f4" }}>
                        {d}% off
                      </span>
                      <span style={{ display: "block", fontSize: 12.5, color: "#8a8f98", marginTop: 2 }}>
                        they pay ~${price}/mo; {tag}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: "13px", color: "#8a8f98", marginTop: 10, lineHeight: 1.55, maxWidth: "52ch" }}>
                Your link carries this first year discount. 60% is the default;
                you can switch it any time on your dashboard.
              </div>
            </div>
          </div>

          <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: "18px", padding: "34px 28px", textAlign: "center" }}>
            <div style={{ fontFamily: MONO, fontSize: 11, letterSpacing: ".18em",
              textTransform: "uppercase", color: "#6b6f78" }}>You earn</div>
            <div style={{ fontFamily: MONO, fontSize: "clamp(38px,4.6vw,56px)", color: ACCENT, lineHeight: 1, marginTop: 12 }}>
              ${monthly.toLocaleString()}
            </div>
            <div style={{ fontFamily: SERIF, fontSize: "20px", marginTop: 8 }}>per month</div>

            <div style={{ height: 1, background: "rgba(255,255,255,0.09)", margin: "22px 0" }} />

            <div style={{ fontFamily: MONO, fontSize: "clamp(28px,3.4vw,40px)", color: ACCENT, lineHeight: 1 }}>
              ${windowTotal.toLocaleString()}
            </div>
            <div style={{ fontSize: "14px", color: "#b6b9c0", marginTop: 8 }}>
              across their {months} month windows
            </div>

            <div style={{ fontSize: "12.5px", color: "#8a8f98", marginTop: 18, lineHeight: 1.55 }}>
              You bring <b style={{ color: "#b6b9c0" }}>{members.toLocaleString()}</b> members now.
              Nobody pays yet; matching has not started. Full price is estimated
              at ${EST_PRICE}. Your people pay about ${year1Price} in year one
              ({refDiscount}% off, your link&apos;s discount), and you are
              paid {pct}% of what is actually collected.
            </div>
          </div>
        </div>
      </section>

      <div style={{ maxWidth: "1140px", margin: "0 auto", height: "1px", background: "rgba(255,255,255,0.07)" }} />

      {/* THE DEAL */}
      <section style={{ position: "relative", zIndex: 2, maxWidth: "1140px", margin: "0 auto", padding: "64px 32px 96px" }}>
        <div style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.26em", color: "#6b6f78", textTransform: "uppercase" }}>The deal</div>
        <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(30px,3.6vw,46px)", lineHeight: 1.08, margin: "18px 0 0", maxWidth: "24ch" }}>
          Two tiers. Terms lock per member.
        </h2>
        <div className="al-grid-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "30px", marginTop: "52px" }}>
          {statCard(`${pct}%`, "the Partner rate",
            `Half of what your members actually pay. Standard tier is ${program.default_affiliate_pct ?? 25}% for ${program.default_window_months ?? 6} months.`)}
          {statCard(`${months}mo`, "per referred member",
            "Counted in paid months. The clock starts when they first pay, never restarts, and the rate locks at referral.")}
          {statCard(slots ? String(slots) : "100", "Partner slots",
            "Reviewed, not raffled. Kept by performing: your quota is 2% of the audience you claim, 100 to 600 profiled signups per quarter. Apply in the first 90 days and, if approved as a Partner, you start there.")}
        </div>
        <p style={{ fontSize: "15px", lineHeight: 1.7, color: "#9a9ea7", maxWidth: "70ch", margin: "36px 0 0" }}>
          Membership is estimated around ${EST_PRICE} a month at full price
          once matching begins; the final price is set then. Your link gives
          your people a first year discount: 60% off (about $8) or 40% off
          (about $12). You pick after approval, 60% is the default, and each
          person keeps the discount they claimed. Free signups count now: your
          code stays attached to every person you bring, and the moment they
          start paying, you start earning. Paid on collected money only, a
          canceled month never pays, and payouts land in your Stripe account
          automatically.
        </p>
        <p style={{ fontSize: "15px", lineHeight: 1.7, color: "#9a9ea7", maxWidth: "70ch", margin: "18px 0 0" }}>
          What gets Partner: 20,000 or more people you can actually reach in our
          world (founders, investors, operators), a named channel we can check,
          applied in the first 90 days, slots remaining. A real audience of
          5,000 or more gets Standard. One performance rule for both: hit your
          quota and Partner is yours to take, and to keep.
        </p>
        <p style={{ margin: "18px 0 0" }}>
          <a href="/affiliates/dashboard?preview=1" className="al-link"
             onClick={(e) => { e.preventDefault(); window.location.href = "/affiliates/dashboard?preview=1"; }}
             style={{ color: ACCENT, fontSize: "15px" }}>
            Preview the Partner dashboard you&apos;ll run →
          </a>
        </p>
      </section>

      <div style={{ maxWidth: "1140px", margin: "0 auto", height: "1px", background: "rgba(255,255,255,0.07)" }} />

      {/* HOW IT WORKS */}
      <section style={{ position: "relative", zIndex: 2, maxWidth: "1140px", margin: "0 auto", padding: "96px 32px" }}>
        <div style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.26em", color: "#6b6f78", textTransform: "uppercase" }}>How it works</div>
        <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(30px,3.6vw,46px)", lineHeight: 1.08, margin: "18px 0 0", maxWidth: "22ch" }}>
          Four steps. The last one repeats monthly.
        </h2>
        <div className="al-grid-4" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "24px", marginTop: "52px" }}>
          {step("01", "Make your case", "Apply below; applications are reviewed, not rubber-stamped. Approval creates your link and a Stripe payout email.")}
          {step("02", "Share it your way", "Post your link, or better: host a mini meetup in the Water Cooler. An AMA, office hours, an after-hours room for a community you already run.")}
          {step("03", "They join free", "Joining costs nothing in early access, and your link carries a first year discount for when paying starts. Your code stays on every signup you bring.")}
          {step("04", "You get paid", `When matching begins, you earn ${pct}% of what each member actually pays, every month they stay, for ${months} paid months each.`)}
        </div>
      </section>

      <div style={{ maxWidth: "1140px", margin: "0 auto", height: "1px", background: "rgba(255,255,255,0.07)" }} />

      {/* PLAY IT STRAIGHT */}
      <section style={{ position: "relative", zIndex: 2, maxWidth: "1140px", margin: "0 auto", padding: "96px 32px" }}>
        <div style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.26em", color: "#6b6f78", textTransform: "uppercase" }}>Play it straight</div>
        <div className="al-grid-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "24px", marginTop: "40px" }}>
          {step("·", "Real people only", "Self referrals and sock puppet accounts do not pay. Payouts on suspicious patterns hold for review, and big volumes get a human look.")}
          {step("·", "Say you are an affiliate", "When you promote your link, disclose the relationship. It is the law in the US, and it is also just how we do it.")}
          {step("·", "Paid on collection", "Earnings trail actual payments. Refunds and canceled months never pay, so nobody has to claw anything back.")}
        </div>
      </section>

      {/* CTA */}
      <section style={{ position: "relative", zIndex: 2, maxWidth: "1140px", margin: "0 auto", padding: "40px 32px 120px", textAlign: "center" }}>
        <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(30px,4vw,52px)", lineHeight: 1.06, margin: "0 auto", maxWidth: "18ch" }}>
          {typeof left === "number" && slots ? `${left} Partner slots open.` : "The Partner tier is forming."}
        </h2>
        <div style={{ marginTop: "34px" }}>
          <button onClick={applyForSlot} className="al-btn al-btn-primary"
                  style={{ fontSize: "16px", fontWeight: 600, color: "#0c0d10", background: ACCENT,
                    border: "none", cursor: "pointer", padding: "16px 28px", borderRadius: "999px" }}>
            Apply for a Partner slot
          </button>
        </div>
      </section>

      {/* FOOT */}
      <footer style={{ position: "relative", zIndex: 2, maxWidth: "1140px", margin: "0 auto",
        padding: "0 32px 46px", display: "flex", justifyContent: "space-between", flexWrap: "wrap",
        gap: "12px", fontSize: "13px", color: "#7c818b" }}>
        <span>Glonet · Mind Machine Learning, Inc.</span>
        <span>
          <a href="/hosts" className="al-link" onClick={(e) => { e.preventDefault(); navigate("/hosts"); }}>Hosts</a>
          {" · "}
          <a href="/signin" className="al-link" onClick={(e) => { e.preventDefault(); navigate("/signin"); }}>Sign in</a>
        </span>
      </footer>

      {applyOpen && (
        <AffiliateApplyDialog onClose={() => { setApplyOpen(false); refreshMyStatus(); }} />
      )}
    </div>
  );
}
